/**
 * Configuração do Firebase, Remote Config e Analytics para meiuca-app-web-react
 *
 * Este arquivo:
 * 1. Inicializa o Firebase App com credenciais específicas do projeto
 * 2. Registra o FirebaseRemoteConfigProvider no Remote Config Service
 * 3. Registra os providers de Analytics (Firebase/GA4 + Mixpanel) — o serviço
 *    canônico faz fan-out, então cada evento vai para os dois (dual-write)
 * 4. Inicializa os serviços com valores padrão personalizados
 */

import {
  initializeFirebaseApp,
  getFirebaseApp,
  isFirebaseEnabled,
  FirebaseRemoteConfigProvider,
  FirebaseAnalyticsProvider,
  type FirebaseConfig
} from '@meiuca/firebase';

import { remoteConfigService } from '@meiuca/remote-config';
import { analyticsService } from '@meiuca/analytics';
import { initializeMixpanel, MixpanelAnalyticsProvider } from '@meiuca/mixpanel';
import { appPreferencesManager } from '@/managers/appPreferencesManager';

// Configuração do Firebase específica para meiuca-app-web-react. As envs
// usam o prefixo padrão `VITE_FIREBASE_*` — mesmo das outras apps web e
// do `firebaseMessagingSwPlugin`, que precisa ler as mesmas chaves pra
// renderizar o service worker.
const firebaseConfig: FirebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Inicializa o Firebase App com a configuração deste projeto
initializeFirebaseApp(firebaseConfig);

// Registra o Firebase como provider de Remote Config
const firebaseRemoteConfigProvider = new FirebaseRemoteConfigProvider();
remoteConfigService.registerProvider(firebaseRemoteConfigProvider);

// Registra o Firebase como provider de Analytics
const firebaseAnalyticsProvider = new FirebaseAnalyticsProvider();
analyticsService.registerProvider(firebaseAnalyticsProvider);

// Mixpanel (product analytics) — dual-write ao lado do Firebase/GA4. Liga/desliga
// geral por VITE_MIXPANEL_ENABLED (por ambiente): ligado em prod, desligado em dev.
// A SDK só carrega quando ligado e com token presente.
initializeMixpanel({
  token: import.meta.env.VITE_MIXPANEL_TOKEN,
  apiHost: import.meta.env.VITE_MIXPANEL_API_HOST || undefined,
  debug: import.meta.env.DEV
});
analyticsService.registerProvider(
  new MixpanelAnalyticsProvider({
    enabled: import.meta.env.VITE_MIXPANEL_ENABLED === 'true'
  })
);

/**
 * Valores padrão das feature flags do app
 */
const APP_FEATURE_FLAGS_DEFAULTS = {
  bookingMaxDaysInAdvance: 15,
  enableSocialLogin: false,
  enablePasswordReset: true,
  enableRegistration: true,
  enableDebugMenu: import.meta.env.DEV
};

/**
 * Inicializa o Remote Config com configurações específicas do app
 *
 * O Remote Config usa o provider registrado acima (Firebase) e
 * fornece valores padrão como fallback se o Firebase não estiver disponível
 */
export async function initializeRemoteConfig() {
  await remoteConfigService.initialize({
    // Valores padrão do app (usados se provider não retornar valor)
    defaultValues: APP_FEATURE_FLAGS_DEFAULTS,
    // Opções específicas do Firebase provider
    providerOptions: {
      isDevelopment: import.meta.env.DEV,
      defaultConfig: APP_FEATURE_FLAGS_DEFAULTS
    }
  });
}

/**
 * Inicializa o Analytics com configurações específicas do app
 *
 * O Analytics usa o provider registrado acima (Firebase Analytics)
 */
export async function initializeAnalytics() {
  await analyticsService.initialize({
    environment: import.meta.env.PROD ? 'prod' : 'dev',
    platform: 'web',
    debug: import.meta.env.DEV, // Loga eventos no console em dev
    providerOptions: {
      isDevelopment: import.meta.env.DEV,
      forceEnable: false // Desabilita analytics em dev
    }
  });

  // Honor the user's analytics opt-out (LGPD). Default granted (legitimate interest).
  analyticsService.setTrackingEnabled(appPreferencesManager.getAnalyticsConsent());
}

// Re-exporta utilitários dos módulos compartilhados
export { getFirebaseApp, isFirebaseEnabled };

// Re-exporta os serviços (já inicializados com providers do app)
export { remoteConfigService } from '@meiuca/remote-config';
export { analyticsService } from '@meiuca/analytics';
